/**
 * The tick's agent step (D234, actors round D) — every hostile foe within
 * reach strikes once per executed turn (`docs/design/actors.md` §6: "the
 * foe strikes on the tick").
 *
 * @remarks
 * Step 6c of the turn, after the scheduler's tick and the reveal watcher
 * and before the triggers, so a foe strikes AFTER the player's own action
 * — the player first, and a killing blow lands before the counter-strike —
 * and a trigger judging the turn sees the blood. Once per executed turn:
 * `wait` is not safe, `look` is not safe, and running is — a foe that is no
 * longer present when the tick comes round has nobody to strike, which is
 * how leaving the room stops it, and how a foe that does not follow is
 * escapable with one step.
 *
 * What a strike says: the foe's `attackNarratives` (else `foeStrikes`),
 * then the FACT — `playerHurt` with `{damage}` and `{health}`, the facility
 * stamping a reading, or `playerUnhurt` when what the player wears turns
 * all of it. Facts, no gauge (the arc's rule for the gas holds here).
 *
 * Death: health at or below nothing selects the ending EXACTLY as
 * `set_ending` would — the foe's own `deathEndingId` (Calder's §5.4: the
 * gas's prose would be a lie about an animal), else the manifest's — and
 * nothing strikes a dead player: the world is closed, and step 7c's return
 * to the checkpoint, if the ending asks for one, puts back the health the
 * photograph holds (ruling 4).
 *
 * In a pack that declares no `player` nothing here can run: the loader
 * refuses a foe without a body to strike, and a world with no health has no
 * one to hurt.
 *
 * @packageDocumentation
 */

import type { DefaultResponseSlot, RuntimeState, RuntimeTranscriptEntry } from '@orpheus/content-schema';

import type { EngineGameBundle } from '../bundle/engine-game-bundle.js';

import { speakSlot } from '../defaults/default-responses.js';
import { applyEffect } from '../effects/apply-effect.js';
import { type Foe, hostileFoesPresent, playerHealthOf, strikeDamageOf, withPlayerHealth, wornDefenseOf } from '../entities/actor-combat.js';
import { type SpeakableLine, splitSpokenLines } from './action-handler-result.js';
import { EngineInvariantError } from './engine-invariant-error.js';
import { stampNarrativeLines, type TranscriptCursor } from './transcript-cursor.js';

/** What the agent step did: the strikes spoken, the slots that spoke, the cursor advanced, and the state after. */
export interface FoeStrikesResult {
	readonly cursor: TranscriptCursor;
	readonly entries: readonly RuntimeTranscriptEntry[];
	/** D58: the slots the strikes spoke through, for the turn's one usage bump. */
	readonly spokenSlots: readonly DefaultResponseSlot[];
	readonly state: RuntimeState;
}

/**
 * Let every hostile foe within reach strike, in declared order, stopping at
 * the player's death.
 *
 * @param bundle - The loaded bundle
 * @param state - The state after the scheduler's tick
 * @param cursor - The turn's cursor, past the tick's entries
 * @returns The strikes, and the state after them
 */
export const strikeAsHostileFoes = (bundle: EngineGameBundle, state: RuntimeState, cursor: TranscriptCursor): FoeStrikesResult => {
	const playerHealth = playerHealthOf(bundle, state);

	if (playerHealth === undefined) {
		return { cursor, entries: [], spokenSlots: [], state };
	}

	const { playerHealth: _spent, ...struck } = hostileFoesPresent(bundle, state).reduce<FoeStrikesStep>(
		(sofar, foe) => (sofar.playerHealth === 0 ? sofar : withStrikeOf(bundle, sofar, foe)),
		{ cursor, entries: [], playerHealth, spokenSlots: [], state },
	);

	return struck;
};

/** The step as it threads through the foes: the result so far, and what the player has left — the number every strike reads and writes. */
interface FoeStrikesStep extends FoeStrikesResult {
	readonly playerHealth: number;
}

/**
 * One foe's strike: its line, the fact, the health written, and the death
 * when there is one.
 *
 * @param bundle - The loaded bundle
 * @param sofar - The step so far
 * @param foe - The striking foe
 * @returns The step with this strike added
 */
const withStrikeOf = (bundle: EngineGameBundle, sofar: FoeStrikesStep, { actor, combat }: Foe): FoeStrikesStep => {
	const damageTaken = strikeDamageOf(combat.damage, wornDefenseOf(bundle, sofar.state));
	const remaining = Math.max(0, sofar.playerHealth - damageTaken);
	const struck = withPlayerHealth(bundle, sofar.state, remaining);
	const fact: SpeakableLine =
		damageTaken === 0
			? speakSlot(bundle, sofar.state, 'playerUnhurt', { itemName: actor.name })
			: speakSlot(bundle, sofar.state, 'playerHurt', { damage: String(damageTaken), health: String(remaining), itemName: actor.name });
	const { lines, spokenSlots } = splitSpokenLines([...(combat.attackNarratives ?? [speakSlot(bundle, sofar.state, 'foeStrikes', { itemName: actor.name })]), fact]);
	const spoken = stampNarrativeLines(bundle, struck, sofar.cursor, lines, `actor_strikes:${actor.actorId}`);
	const stepped: FoeStrikesStep = {
		cursor: spoken.cursor,
		entries: [...sofar.entries, ...spoken.entries],
		playerHealth: remaining,
		spokenSlots: [...sofar.spokenSlots, ...spokenSlots],
		state: struck,
	};

	if (remaining > 0) {
		return stepped;
	}

	// Death selects the ending as `set_ending` would — the foe's own, else the pack's.
	const endingId = combat.deathEndingId ?? bundle.contentPack.manifest.player?.deathEndingId;

	if (endingId === undefined) {
		throw new EngineInvariantError(
			`${actor.actorId} killed the player and no ending answers it — the manifest declares no player.deathEndingId, which the loader's combat gate should have refused`,
		);
	}

	const ended = applyEffect({ endingId, type: 'set_ending' }, bundle, stepped.state, stepped.cursor, `actor_strikes:${actor.actorId}`);

	return { ...stepped, cursor: ended.cursor, entries: [...stepped.entries, ...ended.entries], state: ended.state };
};
